/*
 * TCS-2400 Communications Card
 * A fake Hayes-compatible modem peripheral for apple2js.
 *
 * The 6502 sees a dumb byte pipe with a status register. All Hayes AT
 * command handling happens here in JS, exactly as it would in real modem
 * firmware -- which means the Apple II side stays a genuinely dumb
 * terminal, the way a 1986 terminal program actually was.
 *
 * Anything that could be used to skip ahead (the phone book, the
 * connect delays, the BBS itself) lives on the server. This file should
 * be safe to read in devtools.
 *
 *   Register map, slot n, base = $C080 + n*$10
 *
 *   +0  DATA     read: next RX byte (7-bit)   write: TX byte
 *   +1  STATUS   read only
 *                 b7  RX byte available
 *                 b6  TX ready (always 1)
 *                 b5  DCD -- carrier detect
 *                 b4  BUSY -- dialing or request in flight
 *                 b0  RI  -- ring indicator (unused)
 *   +2  CONTROL  write only
 *                 $01  clear FIFOs
 *                 $02  hang up (drop DTR)
 *   +4..+7  TOKEN    session token, little endian
 *   +3, +8..+F   unused, read as 0
 *
 * The token is held here but advanced on the Apple II, by TOKEN on the
 * disk. That split is deliberate: the host will only act on a request
 * carrying the right token, and working out how to produce one means
 * reversing 6502 on the disk image rather than reading this file or
 * watching the network tab. Forging requests is a legitimate way to
 * work for it; copying a curl command out of devtools is not.
 */

import { TOKEN_ROM } from './tokenrom.js';

// 8N1, so ten bits to the character.
const BAUD_TABLE = { 300: 30, 1200: 120, 2400: 240 };

/*
 * The line rate, in one place so it can be moved back.
 *
 * 1200 was the mainstream figure in 1986 and is the more atmospheric
 * choice; 2400 existed (the Smartmodem 2400 shipped in 1985, V.22bis
 * was standardised in 1984) and is entirely plausible for a government
 * facility, which is what answers both numbers in this game.
 *
 * The practical argument is that a full aperture panel is about 800
 * bytes, which at 1200 baud takes longer to arrive than the interval
 * between refreshes. Change this and DEFAULT_CONNECT in server.js
 * together, and the banner in term.s to match.
 */
const DEFAULT_BAUD = 2400;

// Hayes numeric result codes, for ATV0.
const NUMERIC = {
  'OK': 0,
  'CONNECT': 1,
  'RING': 2,
  'NO CARRIER': 3,
  'ERROR': 4,
  'CONNECT 1200': 5,
  'NO DIALTONE': 6,
  'BUSY': 7,
  'NO ANSWER': 8,
  'CONNECT 2400': 10,
  'VOICE': 11,
};

// Named timers. Anything schedulable has to be cancellable, or an
// aborted call leaves a result code queued behind it.
const TIMER_FIELD = {
  flush:  'flushTimer',
  poll:   'pollTimer',
  escape: 'escapeTimer',
  dial:   'dialTimer',
};

// The card's firmware, mapped at $C200 when it sits in slot 2. This is
// where the session-token routine lives -- not on the disk, and not in
// this file as readable code. Peripherals of the period carried their
// drivers in ROM, so a card that answers at $Cn00 with 6502 is exactly
// what belongs there.
//
// Generated from token.s by tools/build-token-rom.mjs.

export default class ModemCard {
  /**
   * @param {object} [opts]
   * @param {string} [opts.endpoint]   base URL for the host, e.g. '/api'
   * @param {number} [opts.baud]       simulated line rate (default 2400)
   * @param {boolean} [opts.debug]     log card activity to the console
   * @param {(name: string, detail: any) => void} [opts.onEvent] optional hook: (name, detail) => {}
   *        Fires 'dial', 'progress', 'abort', 'offhook', 'hangup',
   *        'stop-voice', 'connect', 'carrier-lost', and 'speaker'. Use
   *        it to drive WebAudio DTMF tones and the handshake screech.
   */
  constructor(opts = {}) {
    this.endpoint = (opts.endpoint || '/api').replace(/\/$/, '');
    this.baud = opts.baud || DEFAULT_BAUD;
    this.onEvent = opts.onEvent || (() => {});
    this.debug = !!opts.debug;

    this._timers = new Set();
    this._hardReset();
  }

  // ---------------------------------------------------------------- apple2js

  init() {}

  reset() {
    // CTRL-RESET on the Apple II should NOT drop the call. Real modems
    // don't care what the host CPU is doing. This is a nice authentic
    // detail: the operator can reset out of a hung program and reconnect
    // with ATO instead of redialing.
    this._flushFifos();
  }

  ioSwitch(off, val) {
    switch (off & 0x0f) {
      case 0x0:
        if (val === undefined) return this._readData();
        this._writeData(val);
        return 0;
      case 0x1:
        return this._status();
      case 0x2:
        if (val !== undefined) this._control(val);
        return 0;
      case 0x4:
      case 0x5:
      case 0x6:
      case 0x7: {
        const i = (off & 0x0f) - 0x4;
        if (val === undefined) return this.token[i];
        this.token[i] = val & 0xff;
        return 0;
      }
      default:
        return 0;
    }
  }

  read(_page, off) { return TOKEN_ROM[off & 0xff]; }
  write(_page, _off, _val) { /* ROM */ }

  getState() {
    return {
      session: this.session,
      onHook: this.onHook,
      commandMode: this.commandMode,
      echo: this.echo,
      verbose: this.verbose,
      quiet: this.quiet,
      speaker: this.speaker,
      spkLevel: this.spkLevel,
      token: this.token.slice(),
    };
  }

  setState(s) {
    if (!s) return;
    Object.assign(this, s);
    // Without this a restored snapshot carries a stale token and the
    // host stops recognising the session on the next keystroke.
    if (Array.isArray(s.token)) this.token = s.token.slice();
    this.onEvent('speaker', { speaker: this.speaker, level: this.spkLevel });
  }

  // ------------------------------------------------------------ registers

  /** Install a token from the host. Eight hex digits, little endian. */
  _loadToken(hex) {
    const t = parseInt(hex, 16) >>> 0;
    this.token = [t & 0xff, (t >>> 8) & 0xff,
                  (t >>> 16) & 0xff, (t >>> 24) & 0xff];
  }

  /**
   * Ask the host for a fresh token.
   *
   * Needed whenever the card has consumed bytes the host never saw. The
   * terminal advances the token over everything it writes, but an
   * escape sequence is swallowed here by definition -- the three plus
   * signs are the one thing a modem must not pass on -- and so is every
   * AT command typed while offline. By the time ATO puts the line back
   * into data mode the two ends can be many bytes apart, and the next
   * keystroke would be rejected as a forgery.
   *
   * Reissuing costs nothing in security: a fresh token still has to be
   * advanced correctly over the next request, which still requires the
   * algorithm.
   */
  _resync() {
    if (!this.session) return;

    this.resyncing = (async () => {
      try {
        const r = await fetch(`${this.endpoint}/resync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session: this.session }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (j.token) this._loadToken(j.token);
      } catch (e) {
        this._log('resync failed', e);
      } finally {
        this.resyncing = null;
      }
    })();
  }

  _status() {
    const now = performance.now();
    let s = 0x40;                                   // TX always ready
    if (this.rx.length && now >= this.nextRxTime) s |= 0x80;
    if (!this.onHook) s |= 0x20;
    if (this.busy) s |= 0x10;
    return s;
  }

  _readData() {
    const now = performance.now();
    if (!this.rx.length || now < this.nextRxTime) return 0;
    // Pace against wall clock, not CPU cycles, so the text crawls at a
    // the real line rate regardless of how fast the emulator is running.
    this.nextRxTime = now + 1000 / (BAUD_TABLE[this.baud] || 120);

    /*
     * Eight bits, not seven.
     *
     * The line is 8-N-1 and always was; the banner says so. Masking to
     * seven was fine while everything crossing it was text, and became
     * a fault the moment a file transfer arrived: a checksum computed
     * over bytes with their top bit knocked off matches nothing, and
     * every block fails.
     *
     * A real 1200 or 2400 baud modem carries whatever it is given.
     */
    return this.rx.shift() & 0xff;
  }

  _writeData(val) {
    // The full byte. AT commands only ever use the low seven, so
    // nothing in command mode notices, and XMODEM's ACK and NAK need
    // the byte they were given.
    const ch = val & 0xff;
    const now = performance.now();

    if (this.commandMode) {
      this._commandChar(ch);
    } else {
      if (this._escapeChar(ch, now)) return;         // swallowed by +++
      this.outBuf.push(ch);
      if (ch === 0x0d) this._flushOut();
      else this._scheduleFlush();
    }
    this.lastTxTime = now;
  }

  _control(val) {
    if (val & 0x01) this._flushFifos();
    if (val & 0x02) this._hangup(false);
  }

  // -------------------------------------------------------- command mode

  _commandChar(ch) {
    if (this.busy) {
      // Any character during dialing aborts the call, like a real modem.
      this._abortDial();
      return;
    }

    // A recording is playing and the operator has touched the keyboard:
    // they have put the handset down. Without this the card is already
    // back on hook after a VOICE result -- the keystroke is just typing
    // at the AT prompt -- so the recording runs to the end with no way
    // to stop it short of ATH0, which nobody would think to try.
    //
    // The key is consumed rather than passed on, and NO CARRIER says so.
    // Letting it through would leave a stray character at the head of
    // the command line, so the next ATDT would be typed onto the end of
    // it and fail for no visible reason. Printing the result makes the
    // key's disappearance an answer instead of a glitch.
    if (this.voiceActive) {
      this.voiceActive = false;
      this.cmdLine = '';
      this.onEvent('stop-voice', {});
      this._result('NO CARRIER');
      return;
    }
    if (ch === 0x0d) {
      const line = this.cmdLine;
      this.cmdLine = '';
      if (this.echo) this._push('\r\n');
      this._command(line);
      return;
    }
    if (ch === 0x08 || ch === 0x7f) {
      if (this.cmdLine.length) {
        this.cmdLine = this.cmdLine.slice(0, -1);
        if (this.echo) this._push('\b \b');
      }
      return;
    }
    if (ch < 0x20) return;
    if (this.cmdLine.length < 60) {
      this.cmdLine += String.fromCharCode(ch);
      if (this.echo) this._push(String.fromCharCode(ch));
    }
  }

  _command(raw) {
    const s = raw.trim().toUpperCase();
    if (s === '') return;                            // bare CR: silence
    if (!s.startsWith('AT')) return this._result('ERROR');

    let body = s.slice(2);
    if (body === '') return this._result('OK');

    let i = 0;
    while (i < body.length) {
      const c = body[i++];

      switch (c) {
        case 'D': {
          // Everything after D is the number. T/P/W/, are dial modifiers.
          const num = body.slice(i);
          return this._dial(num);
        }
        case 'Z':
          this._flushFifos();
          this.onHook = true;
          this.commandMode = true;
          this.session = null;
          this.voiceActive = false;
          this.baud = DEFAULT_BAUD;
          this._factoryDefaults();
          return this._result('OK');
        case 'H': {
          const n = this._digit(body, i); i += n.len;

          if (n.val === 1) {
            // ATH1: seize the line without dialing. You hear dial tone.
            this.onHook = false;
            this.onEvent('offhook', {});
            return this._result('OK');
          }

          // ATH0: hang up.
          //
          // This must ALWAYS emit, even when the card already believes
          // it is on hook. _hangup() returns early in that state, which
          // is correct for the line but wrong for the speaker: after a
          // cancelled dial the card never went off hook, so the early
          // return meant no event fired and a dial tone left playing
          // could not be silenced. ATH0 is the command someone reaches
          // for when something is stuck, so it has to work when the
          // card's own state says there is nothing to stop.
          this._clearTimer('dial');
          this.voiceActive = false;
          if (this.busy) {
            this.dialAborted = true;
            this.busy = false;
          }
          this._hangup(false);
          this.onEvent('hangup', {});
          return this._result('OK');
        }
        case 'O':
          if (this.onHook) return this._result('ERROR');
          this.commandMode = false;
          // Anything typed since the escape was consumed here and never
          // reached the host, so the token has to be reissued.
          this._resync();
          return this._result(this._connectWord());
        case 'E': { const n = this._digit(body, i); i += n.len; this.echo = n.val !== 0; break; }
        case 'V': { const n = this._digit(body, i); i += n.len; this.verbose = n.val !== 0; break; }
        case 'Q': { const n = this._digit(body, i); i += n.len; this.quiet = n.val !== 0; break; }
        case 'X': { const n = this._digit(body, i); i += n.len; break; }

        case 'M': {                             // speaker control
          const n = this._digit(body, i); i += n.len;
          if (n.val > 3) return this._result('ERROR');
          this.speaker = n.val;                 // bare ATM means M0
          this.onEvent('speaker', { speaker: this.speaker,
                                    level: this.spkLevel });
          break;
        }

        case 'L': {                             // speaker volume
          const n = this._digit(body, i); i += n.len;
          if (n.val > 3) return this._result('ERROR');
          this.spkLevel = n.val;                // bare ATL means L0
          this.onEvent('speaker', { speaker: this.speaker,
                                    level: this.spkLevel });
          break;
        }
        case 'I': {
          const n = this._digit(body, i); i += n.len;
          this._push('\r\nTCS-2400  REV 1.4  (C) 1985\r\n');
          break;
        }
        case 'S': {                                   // S-register: accept, ignore
          const m = body.slice(i).match(/^(\d+)(=(\d+))?/);
          if (!m) return this._result('ERROR');
          i += m[0].length;
          break;
        }
        case '&': {
          const sub = body[i++];                      // &F, &C, &D ...
          const n = this._digit(body, i); i += n.len;
          // &F restores the factory profile but, unlike ATZ, does not
          // hang up. Anyone who knows Hayes will try it.
          if (sub === 'F') this._factoryDefaults();
          break;
        }
        case ' ':
          break;
        default:
          return this._result('ERROR');
      }
    }
    return this._result('OK');
  }

  _digit(str, i) {
    const m = str.slice(i).match(/^\d+/);
    return m ? { val: parseInt(m[0], 10), len: m[0].length } : { val: 0, len: 0 };
  }

  // ------------------------------------------------------------- dialing

  async _dial(raw) {
    let digits = raw.replace(/[^0-9]/g, '');
    if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);

    if (!digits.length) return this._result('NO DIALTONE');

    this.busy = true;
    this.voiceActive = false;               // whatever was playing, isn't
    this.onEvent('dial', { digits });

    let plan;
    try {
      const r = await fetch(`${this.endpoint}/dial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ number: digits }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      plan = await r.json();
    } catch (e) {
      this._log('dial failed', e);
      // Losing the network is diegetically perfect: the line just doesn't
      // come up. Delay so it feels like a real ring-out, not an error.
      plan = { result: 'NO CARRIER', delay: 8000 };
    }

    if (this.dialAborted) { this.dialAborted = false; this.busy = false; return; }

    this.onEvent('progress', { digits, ...plan });
    
    this._after(plan.delay ?? 5000, () => {
      if (this.dialAborted) { this.dialAborted = false; this.busy = false; return; }
      this.busy = false;

      // The line's own rate, not the card's default. Different numbers
      // answer at different speeds, and the card has to pace itself to
      // whatever picked up -- which is what a modem negotiating with
      // the far end would have done.
      if (plan.baud && BAUD_TABLE[plan.baud]) this.baud = plan.baud;

      // VOICE means something answered that is not a modem. The card
      // stays on hook, but a recording is now playing and the operator
      // needs a way to cut it short.
      this.voiceActive = String(plan.result || '') === 'VOICE';

      if (String(plan.result || '').startsWith('CONNECT')) {
        this.session = plan.session || null;
        if (plan.token) this._loadToken(plan.token);
        this.onHook = false;
        this.commandMode = false;
        this.onEvent('connect', { digits, session: this.session });
        this._result(plan.result);
        if (plan.out) this._push(plan.out);
        if (plan.hangupAfter) this._after(plan.hangupAfter, () => this._hangup(true));
        if (plan.poll) this._schedulePoll(plan.poll);
      } else {
        this._result(plan.result || 'NO CARRIER');
        if (plan.out) this._push(plan.out);
      }
    }, 'dial');
  }

  _abortDial() {
    this.dialAborted = true;
    this.busy = false;
    // Cancel the scheduled result rather than relying on the flag to
    // neuter it: a redial inside the old delay window would otherwise
    // let the stale timer consume the abort and let the new call
    // through unchecked.
    this._clearTimer('dial');
    this.onEvent('abort', {});                       // stop the ringing
    this._result('NO CARRIER');
  }

  _connectWord() {
    return this.baud === 2400 ? 'CONNECT 2400'
         : this.baud === 300  ? 'CONNECT'
         : 'CONNECT 1200';
  }

  // ------------------------------------------------------- escape / hangup

  /**
   * Hayes +++ escape: one second of silence, three plus signs, one second
   * of silence. Buffer the pluses rather than transmitting them; if the
   * sequence breaks, send them along. Players who know the guard time
   * will be pleased that it's actually enforced.
   */
  _escapeChar(ch, now) {
    const GUARD = 1000;

    if (ch === 0x2b) {
      if (this.plusCount === 0) {
        if (now - this.lastTxTime < GUARD) return false;   // no leading silence
        this.plusCount = 1;
      } else {
        this.plusCount++;
      }
      if (this.plusCount === 3) {
        this._clearTimer('escape');
        this.escapeTimer = this._after(GUARD, () => {
          this.plusCount = 0;
          this.commandMode = true;
          this._result('OK');
        }, 'escape');
      }
      return true;                                          // swallow
    }

    if (this.plusCount) {
      // Sequence broken -- release the pluses we were holding.
      this._clearTimer('escape');
      for (let i = 0; i < this.plusCount; i++) this.outBuf.push(0x2b);
      this.plusCount = 0;
    }
    return false;
  }

  _hangup(pushResult = true) {
    if (this.onHook) return;
    this.onHook = true;
    this.commandMode = true;
    this.session = null;
    this.outBuf = [];
    this._clearTimer('poll');
    this._clearTimer('flush');
    this.onEvent('carrier-lost', {});
    if (pushResult) this._result('NO CARRIER');
  }

  // -------------------------------------------------------- online / server

  _scheduleFlush() {
    this._clearTimer('flush');
    // Short debounce so single-keypress menus work without a CR.
    this.flushTimer = this._after(120, () => this._flushOut(), 'flush');
  }

  async _flushOut() {
    this._clearTimer('flush');
    if (this.onHook) { this.outBuf = []; return; }

    const data = String.fromCharCode(...this.outBuf);
    this.outBuf = [];
    await this._exchange(data);
  }

  _schedulePoll(ms) {
    this._clearTimer('poll');
    this.pollTimer = this._after(ms, () => {
      if (!this.onHook) this._exchange('');
    }, 'poll');
  }

  async _exchange(data) {
    if (this.onHook) return;

    // A resync may still be in flight if the operator typed straight
    // after ATO. Sending now would use the token that is about to be
    // replaced, and the host would drop the line for a mismatch caused
    // entirely by our own timing.
    if (this.resyncing) {
      try { await this.resyncing; } catch (e) { /* reported already */ }
      if (this.onHook) return;
    }

    const session = this.session;
    this.busy = true;
    try {
      // Top sixteen bits of the token, as the Apple II left it after
      // advancing over every byte in this request.
      const tok = (((this.token[3] << 8) | this.token[2]) & 0xffff)
        .toString(16).toUpperCase().padStart(4, '0');

      const r = await fetch(`${this.endpoint}/tty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session, data, token: tok }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      this.busy = false;
      if (session !== this.session) return;          // hung up mid-flight
      if (j.out) this._push(j.out);
      if (j.hangup) {
        // Let the queued bytes drain before dropping carrier, so the
        // the caller actually reads the goodbye message.
        const drain = (this.rx.length / (BAUD_TABLE[this.baud] || 120)) * 1000;
        this._after(drain + 200, () => this._hangup(true));
      } else if (j.poll) {
        this._schedulePoll(j.poll);
      }
    } catch (e) {
      this.busy = false;
      this._log('tty failed', e);
      this._hangup(true);
    }
  }

  // -------------------------------------------------------------- plumbing

  _result(word) {
    if (this.quiet) return;
    if (this.verbose) {
      this._push(`\r\n${word}\r\n`);
    } else {
      const code = NUMERIC[word];
      this._push(`${code === undefined ? 4 : code}\r`);
    }
  }

  _push(str) {
    const now = performance.now();
    if (!this.rx.length && this.nextRxTime < now) this.nextRxTime = now;
    for (let i = 0; i < str.length; i++) {
      this.rx.push(str.charCodeAt(i) & 0xff);
    }
  }

  _flushFifos() {
    this.rx = [];
    this.outBuf = [];
    this.cmdLine = '';
    this.plusCount = 0;
    this.nextRxTime = 0;
  }

  _hardReset() {
    for (const t of this._timers) clearTimeout(t);
    this._timers.clear();

    this.rx = [];
    this.outBuf = [];
    this.cmdLine = '';
    this.nextRxTime = 0;
    this.lastTxTime = 0;
    this.plusCount = 0;

    this.onHook = true;
    this.commandMode = true;
    this.busy = false;
    this.dialAborted = false;
    this.voiceActive = false;

    this.echo = true;
    this.verbose = true;
    this.quiet = false;
    this.speaker = 1;               // ATM1: on until carrier detect
    this.spkLevel = 2;              // ATL2: medium

    this.session = null;
    this.token = [0, 0, 0, 0];          // set by the host on connect
    this.resyncing = null;              // a token request in flight
    this.flushTimer = null;
    this.pollTimer = null;
    this.escapeTimer = null;
    this.dialTimer = null;
  }

  /** The profile ATZ and AT&F both restore. */
  _factoryDefaults() {
    this.echo = true;
    this.verbose = true;
    this.quiet = false;
    this.speaker = 1;
    this.spkLevel = 2;
    this.onEvent('speaker', { speaker: this.speaker, level: this.spkLevel });
  }

  _after(ms, fn, name) {
    const id = setTimeout(() => {
      this._timers.delete(id);
      if (name) this[TIMER_FIELD[name]] = null;
      fn();
    }, ms);
    this._timers.add(id);
    if (name) this[TIMER_FIELD[name]] = id;
    return id;
  }

  _clearTimer(name) {
    const field = TIMER_FIELD[name];
    if (!field) return;
    const id = this[field];
    if (id) { clearTimeout(id); this._timers.delete(id); }
    this[field] = null;
  }

  _log(...a) { if (this.debug) console.log('[TCS-2400]', ...a); }
}
